import { useState, useEffect, useMemo, useContext } from 'react'
import { Container, Table, Button, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../AuthContext'
import Cookies from 'js-cookie'
import axios from 'axios'

// 任務類型對照表
const TASK_TYPES = {
  0: '問卷',
  1: '飲水',
  2: '飲食',
  3: '心情',
  4: '排便',
  5: '睡眠',
  6: '運動',
  7: '血壓',
  8: '步行 6 分鐘',
  9: '授權書',
  10: '授權書列表',
  11: '通用',
  12: '血糖',
  13: '飲食搭配圖片上傳',
  14: '健檢報告上傳',
  15: '預約健檢平台',
  16: '處方箋啟動任務',
  17: '體重',
  18: '腰臀圍',
  99: 'APP 操作',
}

// 狀態膠囊組件 - 參考 PatientData 的 ProgramPill
const StatusPill = ({ status }) => {
  const colors = {
    active: { bg: '#e8f5e9', color: '#2e7d32', border: '#c8e6c9' },
    ended: { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' },
    draft: { bg: '#fff3e0', color: '#f57c00', border: '#ffe0b2' },
    deleted: { bg: '#ffebee', color: '#d32f2f', border: '#ffcdd2' },
  }

  const statusText = {
    active: '上架中',
    ended: '已結束',
    draft: '暫存中',
    deleted: '已刪除',
  }

  const style = colors[status] || colors.ended

  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        display: 'inline-block',
        fontSize: '14px',
      }}
    >
      {statusText[status] || '未知'}
    </span>
  )
}

// 任務類型膠囊組件
const getTaskTypeStyle = (taskType) => {
  const colors = {
    0: { bg: '#e3f2fd', color: '#1976d2', border: '#bbdefb' },
    1: { bg: '#e8f5e8', color: '#388e3c', border: '#c8e6c9' },
    2: { bg: '#fff3e0', color: '#f57c00', border: '#ffcc02' },
    3: { bg: '#fce4ec', color: '#c2185b', border: '#f8bbd9' },
    4: { bg: '#f3e5f5', color: '#7b1fa2', border: '#e1bee7' },
    5: { bg: '#e0f2f1', color: '#00796b', border: '#b2dfdb' },
    6: { bg: '#fff8e1', color: '#fbc02d', border: '#fff176' },
    7: { bg: '#ffebee', color: '#d32f2f', border: '#ffcdd2' },
    8: { bg: '#fafafa', color: '#616161', border: '#e0e0e0' },
    9: { bg: '#f1f8e9', color: '#689f38', border: '#dcedc8' },
    10: { bg: '#e8eaf6', color: '#3f51b5', border: '#c5cae9' },
    11: { bg: '#f5f5f5', color: '#424242', border: '#e0e0e0' },
    12: { bg: '#e1f5fe', color: '#0288d1', border: '#b3e5fc' },
    13: { bg: '#f1f8e9', color: '#558b2f', border: '#dcedc8' },
    15: { bg: '#faf2ff', color: '#6a1b9a', border: '#e1bee7' },
    17: { bg: '#fff3e0', color: '#ef6c00', border: '#ffe0b2' },
    18: { bg: '#e8eaf6', color: '#3f51b5', border: '#c5cae9' },
  }

  const style = colors[taskType] || {
    bg: '#f5f5f5',
    color: '#212121',
    border: '#e0e0e0',
  }

  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        display: 'inline-block',
        fontSize: '14px',
      }}
    >
      {TASK_TYPES[taskType] || '未知'}
    </span>
  )
}

const PushTaskList = () => {
  const navigate = useNavigate()

  const [taskList, setTaskList] = useState([])
  const [filteredTasks, setFilteredTasks] = useState([])
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showRecipientsModal, setShowRecipientsModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedRecipients, setSelectedRecipients] = useState([])
  // 推送對象搜尋手機
  const [searchPhone, setSearchPhone] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // 獲取 session
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)
  const sessionFromCookie = Cookies.get('session')
  const { session = '' } = sessionFromCookie
    ? JSON.parse(sessionFromCookie)
    : {}

  // 從 API 獲取任務清單
  const fetchMissionList = async () => {
    try {
      setIsLoading(true)
      setError('')

      if (!session) {
        console.warn('Session not found for mission list fetch')
        setError('請先登入')
        setLoginShow(true)
        return
      }

      const response = await axios.get(
        `${baseUrl}/api/backend/mission/getList`,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: session,
          },
        }
      )

      console.log('任務清單 API 回應:', response.data)

      if (response.data && response.data.data) {
        // 將 API 資料轉換成前端需要的格式
        const transformedTasks = response.data.data.map((mission) => {
          // 判斷任務狀態的邏輯
          const getTaskStatus = (mission) => {
            // 1. 若已刪除則以已刪除為優先
            if (mission.is_deleted === 1) {
              return 'deleted'
            }

            // 2. 若同時遇到暫存中且已結束(以上架時間判斷)則以暫存中為優先
            if (mission.status === 0) {
              // 未上架(暫存)
              return 'draft'
            }

            // 3. 判斷已上架的任務是否已結束
            if (mission.status === 1) {
              // 已上架
              // 檢查是否已過結束時間
              if (mission.end_time) {
                const endTime = new Date(mission.end_time)
                const now = new Date()
                if (now > endTime) {
                  return 'ended' // 已結束
                }
              }
              return 'active' // 上架中
            }

            // 預設返回暫存中
            return 'draft'
          }

          return {
            id: mission.mission_id,
            taskType: mission.type,
            taskTitle: mission.name,
            taskDescription: mission.description,
            remark: mission.comment || '',
            // 預設推送對象格式，實際可能需要根據其他 API 調整
            recipients: {
              sendType: mission.send_type, // 0=全部、1=聯絡人清單、2=會員清單
              type:
                mission.send_type === 0
                  ? 'all'
                  : mission.send_type === 1
                    ? 'contacts'
                    : 'members',
              phones: [], // 初始為空，點擊時再透過 API 獲取
              memberGroups: [], // 會員群組清單，點擊時獲取
              memberGroupId: null, // 聯絡人清單的群組 ID，需要時再設定
            },
            status: getTaskStatus(mission), // 使用新的狀態判斷邏輯
            statusDescription: mission.status_description,
            startTime: mission.start_time,
            endTime: mission.end_time,
            isDeleted: mission.is_deleted,
          }
        })

        setTaskList(transformedTasks)
        extendCookie() // 延長 session
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error('未收到預期的資料格式')
      }
    } catch (err) {
      console.error('獲取任務清單失敗:', err)

      setError('獲取任務清單時發生錯誤')
    } finally {
      setIsLoading(false)
    }
  }

  // 獲取聯絡人群組詳細資訊
  const fetchMemberGroup = async (memberGroupId) => {
    try {
      if (!session) {
        console.warn('Session not found for member group fetch')
        return null
      }

      const response = await axios.post(
        `${baseUrl}/api/backend/memberGroup/get`,
        { member_group_id: memberGroupId.toString() },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: session,
          },
        }
      )

      console.log('聯絡人群組 API 回應:', response.data)

      if (response.data) {
        const group = response.data
        return {
          id: group.member_group_id,
          name: group.name,
          tags: group.tags || [],
          description: group.description || '',
          count: group.count,
          memberList: group.member_list || [], // 手機號碼清單
        }
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      }

      extendCookie() // 延長 session
      return null
    } catch (err) {
      console.error('獲取聯絡人群組失敗:', err)

      alert('獲取聯絡人群組時發生錯誤')

      return null
    }
  }

  // 獲取特定任務的詳細資訊（包含推送對象清單）
  const fetchMissionDetail = async (missionId) => {
    try {
      if (!session) {
        console.warn('Session not found for mission detail fetch')
        return null
      }

      const response = await axios.post(
        `${baseUrl}/api/backend/mission/get`,
        { mission_id: missionId },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: session,
          },
        }
      )

      console.log('任務詳情 API 回應:', response.data)

      if (response.data) {
        const mission = response.data
        return {
          id: mission.mission_id,
          taskTitle: mission.name,
          taskComment: mission.comment || '',
          taskDescription: mission.description,
          recipients: {
            sendType: mission.send_type,
            type:
              mission.send_type === 0
                ? 'all'
                : mission.send_type === 1
                  ? 'contacts'
                  : 'members',
            phones: mission.member_list || [], // 手機號碼清單
            memberGroups: mission.member_group_list || [], // 會員群組清單
          },
          excludeMemberList: mission.exclude_member_list || [],
          sendType: mission.send_type,
        }
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      }

      extendCookie() // 延長 session
      return null
    } catch (err) {
      console.error('獲取任務詳情失敗:', err)

      alert('獲取任務詳情時發生錯誤')

      return null
    }
  }

  // 組件載入時獲取任務清單
  useEffect(() => {
    fetchMissionList()
  }, [session])

  // 過濾任務
  useEffect(() => {
    let filtered = taskList
    if (statusFilter !== 'all') {
      filtered = taskList.filter((task) => task.status === statusFilter)
    }
    setFilteredTasks(filtered)
    setCurrentPage(1) // 重置到第一頁
  }, [taskList, statusFilter])

  // 格式化時間顯示
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '無結束時間'
    return dateTimeStr
  }

  // 顯示推送對象詳細資訊
  const showRecipientsDetail = async (task) => {
    setSelectedTask(task)
    setSelectedRecipients([]) // 重置選取狀態
    setSearchPhone('') // 重置搜尋
    setShowRecipientsModal(true)

    const missionDetail = await fetchMissionDetail(task.id)

    if (!missionDetail) {
      console.error('無法獲取任務詳情')
      return
    }

    // 無論什麼推送類型，都要保存排除清單
    const excludeList = missionDetail.excludeMemberList || []

    // 如果是全部推送
    if (task.recipients.sendType === 0) {
      setSelectedTask((prev) => ({
        ...prev,
        excludeMemberList: excludeList, // 保存排除清單供 UI 顯示
        recipients: {
          ...prev.recipients,
          type: 'all',
          sendType: 0,
          excludedCount: excludeList.length, // 被排除的數量
        },
      }))
      return
    }

    // 如果是聯絡人清單，先獲取任務詳情取得 member_group_id，再獲取群組詳情
    if (task.recipients.sendType === 1) {
      if (
        missionDetail &&
        missionDetail.recipients.memberGroups &&
        missionDetail.recipients.memberGroups.length > 0
      ) {
        console.log('取得群組列表:', missionDetail.recipients.memberGroups)

        try {
          // 對每個群組發送 API 請求獲取詳細資訊
          const groupDetailPromises = missionDetail.recipients.memberGroups.map(
            async (group) => {
              try {
                const groupDetail = await fetchMemberGroup(
                  group.member_group_id
                )
                return {
                  ...group, // 保留原始群組資訊 (member_group_id, name, count, tags)
                  memberList: groupDetail?.memberList || [], // 添加成員清單
                  description: groupDetail?.description || '', // 添加描述
                }
              } catch (error) {
                console.error(
                  `獲取群組 ${group.member_group_id} 詳情失敗:`,
                  error
                )
                return {
                  ...group,
                  memberList: [],
                  description: '',
                }
              }
            }
          )

          // 等待所有群組詳情請求完成
          const groupsWithDetails = await Promise.all(groupDetailPromises)

          // 合併所有群組的電話號碼並去重複
          const allPhones = groupsWithDetails
            .flatMap((group) => group.memberList)
            .filter((phone) => phone) // 過濾空值

          const uniquePhones = [...new Set(allPhones)]

          // 排除對象
          const excludeList = missionDetail.excludeMemberList || []
          const filteredPhones = uniquePhones.filter(
            (phone) => !excludeList.includes(phone)
          )

          console.log('過濾後的電話號碼:', filteredPhones)

          setSelectedTask((prev) => ({
            ...prev,
            recipients: {
              ...prev.recipients,
              phones: filteredPhones, // 所有群組的電話號碼
              memberGroups: groupsWithDetails, // 包含詳細資訊的群組列表
              groupInfo: {
                totalGroups: groupsWithDetails.length,
                totalMembers: filteredPhones.length,
                excludedCount: uniquePhones.length - filteredPhones.length,
                groups: groupsWithDetails.map((group) => ({
                  id: group.member_group_id,
                  name: group.name,
                  count: group.count,
                  tags: group.tags,
                  description: group.description,
                })),
              },
              excludeMemberList: excludeList,
            },
          }))
        } catch (error) {
          console.error('處理群組詳情時發生錯誤:', error)
        }
      }
    }

    // 如果是會員清單，調用 getMission API
    if (task.recipients.sendType === 2) {
      const missionDetail = await fetchMissionDetail(task.id)
      if (missionDetail) {
        setSelectedTask((prev) => ({
          ...prev,
          recipients: missionDetail.recipients,
        }))
      }
    }
  }

  // 編輯任務
  const handleEdit = (taskId) => {
    console.log('編輯任務 ID:', taskId)
    // 🆕 只傳送 taskId，讓 PushTask 組件自己 fetch 完整資料
    navigate('/admin/push-task', {
      state: {
        editMode: true,
        taskId: taskId, // 只傳送 ID
      },
    })
  }

  // 刪除任務確認
  const confirmDelete = (task) => {
    setSelectedTask(task)
    setShowDeleteModal(true)
  }

  // 執行刪除
  const handleDelete = async () => {
    if (!selectedTask) return

    setIsDeleting(true)

    try {
      if (!session) {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
        throw new Error('Session not found')
      }

      // 調用刪除任務 API
      const response = await axios.post(
        `${baseUrl}/api/backend/mission/delete`,
        { mission_id: selectedTask.id },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: session,
          },
        }
      )

      console.log('刪除任務 API 回應:', response.data)

      if (response.data && response.data.message === '已刪除任務') {
        // 延長 session
        extendCookie()

        // 重新獲取任務清單以反映最新狀態
        await fetchMissionList()

        // 顯示成功訊息
        alert(`任務 ${selectedTask.id} 已成功刪除並下架`)

        // 關閉 Modal 並重置狀態
        setShowDeleteModal(false)
        setSelectedTask(null)
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error('未收到預期的刪除成功回應')
      }
    } catch (error) {
      console.error('刪除任務失敗:', error)
      alert('刪除任務失敗，請稍後再試')
    } finally {
      setIsDeleting(false)
    }
  }

  // 分頁計算
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentTasks = filteredTasks.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage)

  // 分頁控制 - 修改為跟 PatientData 一樣的樣式
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  // 格式化上架時間顯示
  const formatTimeRange = (startTime, endTime) => {
    const formatTime = (timeStr) => {
      if (!timeStr) return '無結束時間'
      return timeStr.replace('T', ' ').slice(0, 19) // 移除 T 和毫秒
    }

    const start = formatTime(startTime)
    const end = formatTime(endTime)

    return endTime ? `${start} ~ ${end}` : start
  }

  // 格式化狀態顯示
  const getStatusText = (status) => {
    const statusMap = {
      active: '上架中',
      ended: '已結束',
      draft: '暫存中',
      deleted: '已刪除',
    }
    return statusMap[status] || '未知'
  }

  // 推送對象搜尋手機
  const filteredRecipients = useMemo(() => {
    if (!selectedTask?.recipients?.phones) return []

    const phones = selectedTask.recipients.phones
    if (!searchPhone) return phones

    return phones.filter((phone) =>
      phone.toLowerCase().includes(searchPhone.toLowerCase())
    )
  }, [selectedTask?.recipients?.phones, searchPhone])

  return (
    <Container className="py-4" style={{ maxWidth: '1400px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#2c3e50' }}>推送任務清單</h2>

        {/* 狀態過濾器 */}
        <div className="d-flex gap-2 flex-wrap">
          <span
            onClick={() => setStatusFilter('all')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              backgroundColor: statusFilter === 'all' ? '#e3f2fd' : '#f8f9fa',
              color: statusFilter === 'all' ? '#0d47a1' : '#6c757d',
              border:
                statusFilter === 'all'
                  ? '1px solid #bbdefb'
                  : '1px solid #dee2e6',
              display: 'inline-block',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            全部 ({taskList.length})
          </span>
          <span
            onClick={() => setStatusFilter('active')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              backgroundColor:
                statusFilter === 'active' ? '#e8f5e9' : '#f8f9fa',
              color: statusFilter === 'active' ? '#2e7d32' : '#6c757d',
              border:
                statusFilter === 'active'
                  ? '1px solid #c8e6c9'
                  : '1px solid #dee2e6',
              display: 'inline-block',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            上架中 ({taskList.filter((t) => t.status === 'active').length})
          </span>
          <span
            onClick={() => setStatusFilter('ended')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              backgroundColor: statusFilter === 'ended' ? '#f5f5f5' : '#f8f9fa',
              color: statusFilter === 'ended' ? '#616161' : '#6c757d',
              border:
                statusFilter === 'ended'
                  ? '1px solid #e0e0e0'
                  : '1px solid #dee2e6',
              display: 'inline-block',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            已結束 ({taskList.filter((t) => t.status === 'ended').length})
          </span>
          <span
            onClick={() => setStatusFilter('draft')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              backgroundColor: statusFilter === 'draft' ? '#fff3e0' : '#f8f9fa',
              color: statusFilter === 'draft' ? '#f57c00' : '#6c757d',
              border:
                statusFilter === 'draft'
                  ? '1px solid #ffe0b2'
                  : '1px solid #dee2e6',
              display: 'inline-block',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            暫存中 ({taskList.filter((t) => t.status === 'draft').length})
          </span>
          <span
            onClick={() => setStatusFilter('deleted')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              backgroundColor:
                statusFilter === 'deleted' ? '#ffebee' : '#f8f9fa',
              color: statusFilter === 'deleted' ? '#d32f2f' : '#6c757d',
              border:
                statusFilter === 'deleted'
                  ? '1px solid #ffcdd2'
                  : '1px solid #dee2e6',
              display: 'inline-block',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            已刪除 ({taskList.filter((t) => t.status === 'deleted').length})
          </span>
        </div>
      </div>

      {/* 任務表格 */}
      <div className="bg-white rounded-3">
        <Table hover responsive className="mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: '100px' }}>任務編號</th>
              <th style={{ width: '160px' }}>任務種類</th>
              <th>任務名稱</th>
              <th style={{ width: '200px' }}>任務備註</th>
              <th style={{ width: '200px' }}>任務對象</th>
              <th style={{ width: '200px' }}>上架時間</th>
              <th style={{ width: '100px' }}>狀態</th>
              <th style={{ width: '120px' }}>進階操作</th>
            </tr>
          </thead>
          <tbody>
            {currentTasks.length > 0 ? (
              currentTasks.map((task) => (
                <tr key={task.id}>
                  <td>{task.id}</td>
                  <td>{getTaskTypeStyle(task.taskType)}</td>
                  <td>{task.taskTitle}</td>
                  <td>
                    {task.remark.length > 20
                      ? `${task.remark.substring(0, 20)}...`
                      : task.remark}
                  </td>
                  <td>
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={() => showRecipientsDetail(task)}
                      style={{ color: '#212529' }}
                    >
                      清單
                    </Button>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px' }}>
                      <div>{formatDateTime(task.startTime)}</div>
                      <div className="text-muted">~</div>
                      <div>{formatDateTime(task.endTime)}</div>
                    </div>
                  </td>
                  <td>
                    <StatusPill status={task.status} />
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(task.id)}
                        disabled={task.status === 'deleted'}
                        title="編輯"
                      >
                        <i className="fa-solid fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => confirmDelete(task)}
                        disabled={task.status === 'deleted'}
                        title="刪除"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4 text-muted">
                  <i className="fa-solid fa-inbox fa-2x mb-3"></i>
                  <div>目前沒有符合條件的任務</div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* 分頁控制 - 參考 PatientData 的樣式 */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '40px',
            gap: '8px',
          }}
        >
          <div
            onClick={() => handlePageChange(currentPage - 1)}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              color: currentPage <= 1 ? '#ccc' : '#666',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            <i className="fa-solid fa-angle-left" />
          </div>

          {/* Page numbers with ellipsis */}
          {Array.from({ length: totalPages }).map((_, index) => {
            // Show first page, last page, current page, and pages around current
            const shouldShow =
              index === 0 || // First page
              index === totalPages - 1 || // Last page
              Math.abs(index - (currentPage - 1)) <= 2 // Pages around current (adjust for 0-based index)

            // Show ellipsis instead of page numbers
            if (!shouldShow && (index === 1 || index === totalPages - 2)) {
              return (
                <div key={index} style={{ color: '#666' }}>
                  <i className="fa-solid fa-ellipsis" />
                </div>
              )
            }

            // Hide other page numbers
            if (!shouldShow) {
              return null
            }

            return (
              <div
                key={index}
                onClick={() => handlePageChange(index + 1)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor:
                    currentPage === index + 1 ? '#67c7aa' : 'white',
                  color: currentPage === index + 1 ? 'white' : 'black',
                  cursor: 'pointer',
                }}
              >
                {index + 1}
              </div>
            )
          })}

          <div
            onClick={() => handlePageChange(currentPage + 1)}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              color: currentPage >= totalPages ? '#ccc' : '#666',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            <i className="fa-solid fa-angle-right" />
          </div>
        </div>
      )}

      {/* 刪除確認 Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: 'white',
          }}
        >
          <Modal.Title
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              color: '#2c3e50',
            }}
          >
            刪除任務
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <div>
              <p className="text-center">確定要刪除以下任務嗎？</p>
              <p className="text-center mb-3">
                刪除後，原本上架中的任務將不會出現在原本發送對象的 App 中
              </p>
              <div className="bg-light p-5 rounded">
                <div className="mb-2">
                  ID：
                  {selectedTask.id}
                </div>
                <div className="mb-2">
                  任務種類：
                  {TASK_TYPES[selectedTask.taskType]}
                </div>
                <div className="mb-2">
                  任務名稱：
                  {selectedTask.taskTitle}
                </div>
                <div className="mb-2">
                  任務備註：
                  {selectedTask.remark}
                </div>
                <div className="mb-2">
                  上架時間：
                  {formatTimeRange(
                    selectedTask.startTime,
                    selectedTask.endTime
                  )}
                </div>
                <div className="mb-2">
                  狀態：
                  {getStatusText(selectedTask.status)}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            取消
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            確認刪除
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 推送對象詳細資訊 Modal */}
      <Modal
        show={showRecipientsModal}
        onHide={() => setShowRecipientsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: 'white',
          }}
        >
          <Modal.Title
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              color: '#2c3e50',
            }}
          >
            任務 {selectedTask?.id} 的目標對象清單
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <div>
              <h6 className="mb-3">
                {selectedTask.taskTitle}({selectedTask.remark})
              </h6>

              {selectedTask.recipients.sendType === 0 && (
                <>
                  <div className="alert alert-info">
                    <i className="fa-solid fa-info-circle me-2"></i>
                    此任務將推送給所有會員
                    {selectedTask.excludeMemberList &&
                      selectedTask.excludeMemberList.length > 0 && (
                        <span className="text-muted ms-2">
                          (已排除 {selectedTask.excludeMemberList.length} 人)
                        </span>
                      )}
                  </div>

                  {/* 顯示被排除的電話號碼清單 */}
                  {selectedTask.excludeMemberList &&
                    selectedTask.excludeMemberList.length > 0 && (
                      <div className="mt-3">
                        <h6>已排除的電話號碼</h6>
                        <div
                          style={{
                            padding: '12px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '12px',
                              maxHeight: '100px',
                              overflow: 'auto',
                              fontFamily: 'monospace',
                              color: '#212529',
                            }}
                          >
                            {selectedTask.excludeMemberList.join(', ')}
                          </div>
                          <small className="text-muted d-block mt-2">
                            共 {selectedTask.excludeMemberList.length}{' '}
                            個號碼被排除
                          </small>
                        </div>
                      </div>
                    )}
                </>
              )}

              {selectedTask.recipients.sendType === 2 &&
                selectedTask.recipients.memberGroups &&
                selectedTask.recipients.memberGroups.length > 0 && (
                  <div className="mb-4">
                    <h6>會員群組清單</h6>
                    <div className="border rounded p-2">
                      {selectedTask.recipients.memberGroups.map(
                        (group, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between align-items-center p-2 border-bottom"
                          >
                            <div>
                              <strong>{group.name}</strong>
                              <div className="text-muted small">
                                群組 ID: {group.member_group_id}
                              </div>
                            </div>
                            <span className="badge bg-primary">
                              {group.count} 人
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {(selectedTask.recipients.sendType === 1 ||
                selectedTask.recipients.sendType === 2) &&
                selectedTask.recipients.phones &&
                selectedTask.recipients.phones.length > 0 && (
                  <div>
                    {/* 搜尋列 */}
                    <div className="mb-3">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fa-solid fa-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="請搜尋手機號碼"
                          value={searchPhone}
                          onChange={(e) => setSearchPhone(e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      {/* 表格標題 */}
                      <Table hover responsive className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>目標對象手機號碼</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRecipients.length > 0 ? (
                            filteredRecipients.map((phone, index) => (
                              <tr key={index}>
                                <td>{phone}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="text-center text-muted py-3">
                                {searchPhone
                                  ? '找不到符合的手機號碼'
                                  : '尚無推送對象資料'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>

                      {/* 底部狀態 */}
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <div className="text-muted">
                          共 {selectedTask.recipients.phones.length} 人
                          {searchPhone &&
                            filteredRecipients.length !==
                              selectedTask.recipients.phones.length &&
                            ` (篩選後 ${filteredRecipients.length} 人)`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {(selectedTask.recipients.sendType === 1 ||
                selectedTask.recipients.sendType === 2) &&
                (!selectedTask.recipients.phones ||
                  selectedTask.recipients.phones.length === 0) && (
                  <div className="alert alert-warning">
                    <i className="fa-solid fa-exclamation-triangle me-2"></i>
                    此任務目前沒有推送對象
                  </div>
                )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  )
}

export default PushTaskList
